import React, { useState, useEffect } from 'react';
import { Activity, TrendingUp, Users, FileText, Calendar } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';
import { supabase } from '../../lib/supabase';

interface ChartData {
  timestamp: string;
  value: number;
  label: string;
}

export const RealtimeCharts: React.FC = () => {
  const [projectsData, setProjectsData] = useState<ChartData[]>([]);
  const [usersData, setUsersData] = useState<ChartData[]>([]);
  const [activityData, setActivityData] = useState<ChartData[]>([]);
  const [statsData, setStatsData] = useState({
    projectsToday: 0,
    usersToday: 0,
    documentsToday: 0,
    meetingsToday: 0,
  });

  useEffect(() => {
    initializeData();
    loadTodayStats();
    const interval = setInterval(loadTodayStats, 30000);
    return () => clearInterval(interval);
  }, []);

  useRealtimeSubscription({
    table: 'projects',
    event: 'INSERT',
    onInsert: (payload) => {
      updateChartData(setProjectsData, 'Projet créé');
      setStatsData(prev => ({ ...prev, projectsToday: prev.projectsToday + 1 }));
    },
  });

  useRealtimeSubscription({
    table: 'users',
    event: 'INSERT',
    onInsert: (payload) => {
      updateChartData(setUsersData, 'Utilisateur inscrit');
      setStatsData(prev => ({ ...prev, usersToday: prev.usersToday + 1 }));
    },
  });

  useRealtimeSubscription({
    table: 'project_documents',
    event: 'INSERT',
    onInsert: () => {
      updateChartData(setActivityData, 'Document ajouté');
      setStatsData(prev => ({ ...prev, documentsToday: prev.documentsToday + 1 }));
    },
  });

  useRealtimeSubscription({
    table: 'meetings',
    event: 'INSERT',
    onInsert: () => {
      setStatsData(prev => ({ ...prev, meetingsToday: prev.meetingsToday + 1 }));
    },
  });

  const initializeData = () => {
    const now = new Date();
    const initialData: ChartData[] = [];

    for (let i = 29; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60000);
      initialData.push({
        timestamp: time.toISOString(),
        value: 0,
        label: time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      });
    }

    setProjectsData([...initialData]);
    setUsersData([...initialData]);
    setActivityData([...initialData]);
  };

  const updateChartData = (
    setter: React.Dispatch<React.SetStateAction<ChartData[]>>,
    label: string
  ) => {
    setter(prev => {
      const now = new Date();
      const newData = [...prev];

      newData.shift();

      const lastValue = newData.length > 0 ? newData[newData.length - 1].value : 0;
      newData.push({
        timestamp: now.toISOString(),
        value: lastValue + 1,
        label: now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      });

      return newData;
    });
  };

  const loadTodayStats = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { count: projectsCount } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today.toISOString());

      const { count: usersCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today.toISOString());

      const { count: documentsCount } = await supabase
        .from('project_documents')
        .select('*', { count: 'exact', head: true })
        .gte('uploaded_at', today.toISOString());

      const { count: meetingsCount } = await supabase
        .from('meetings')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today.toISOString());

      setStatsData({
        projectsToday: projectsCount || 0,
        usersToday: usersCount || 0,
        documentsToday: documentsCount || 0,
        meetingsToday: meetingsCount || 0,
      });
    } catch (error) {
      console.error('Error loading today stats:', error);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, value, icon, color }) => (
    <div className="bg-white rounded-lg border border-gray-100 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center`}>
          {icon}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Projets aujourd'hui"
          value={statsData.projectsToday}
          icon={<FileText className="w-6 h-6 text-white" />}
          color="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Utilisateurs aujourd'hui"
          value={statsData.usersToday}
          icon={<Users className="w-6 h-6 text-white" />}
          color="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Documents aujourd'hui"
          value={statsData.documentsToday}
          icon={<FileText className="w-6 h-6 text-white" />}
          color="bg-gradient-to-br from-orange-500 to-orange-600"
        />
        <StatCard
          title="RDV aujourd'hui"
          value={statsData.meetingsToday}
          icon={<Calendar className="w-6 h-6 text-white" />}
          color="bg-gradient-to-br from-purple-500 to-purple-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Projets créés (30 min)</h3>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs text-gray-600">En direct</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={projectsData}>
              <defs>
                <linearGradient id="colorProjects" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#3b82f6"
                fillOpacity={1}
                fill="url(#colorProjects)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Inscriptions (30 min)</h3>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs text-gray-600">En direct</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={usersData}>
              <defs>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#10b981"
                fillOpacity={1}
                fill="url(#colorUsers)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Activité globale (30 min)</h3>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs text-gray-600">Temps réel</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                data={activityData}
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="value"
                data={projectsData}
                stroke="#3b82f6"
                strokeWidth={2}
                name="Projets"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="value"
                data={usersData}
                stroke="#10b981"
                strokeWidth={2}
                name="Utilisateurs"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="value"
                data={activityData}
                stroke="#f59e0b"
                strokeWidth={2}
                name="Documents"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl border border-blue-200 p-6">
        <div className="flex items-center space-x-3">
          <Activity className="w-6 h-6 text-blue-600" />
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Mise à jour automatique</h4>
            <p className="text-sm text-gray-600">
              Les graphiques se mettent à jour automatiquement lorsque de nouvelles données arrivent
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
